import { useMutation, useQuery } from '@tanstack/react-query';
import fetcher, {
	FleetFile,
	FleetGetListParams,
	FleetPaginated,
	FleetSearch,
} from '../fetcher';
import { MasterData } from './master-data';
import { FilePayload } from './types';

export type SLASearchModule = 'application' | 'disbursement';

export const useMonitoringSLASearchOptions = (module?: SLASearchModule) => {
	return useQuery({
		queryKey: ['/monitoring-sla/get-search-options', module] as const,
		queryFn: ({ queryKey }) => {
			const [api, module] = queryKey;
			return fetcher<{
				moduleOptions: MasterData[];
				searchByOptions: MasterData[];
			}>({
				api,
				body: {
					module,
				},
			});
		},
	});
};

export type MonitoringSLASearchParam = FleetGetListParams & {
	startDate: string;
	endDate: string;
	module: SLASearchModule;
};

export const useMonitoringSLASearch = (param: MonitoringSLASearchParam) => {
	return useQuery({
		queryKey: ['/monitoring-sla/search-sla', param] as const,
		queryFn: ({ queryKey }) => {
			const [api, body] = queryKey;
			return fetcher<{
				applications: {
					key: number;
					customerId: string;
					customerName: string;
					bmppGroupId: string | null;
					bmppGroupName: string | null;
					frmName: string;
					fbdhName: string;
					divisionName: string;
					plafonType: string;
					orderSource: string;
					plafonAmount: number;
					prospectId: string;
					prospectStatus: string;
					napNo: string;
					spfNo: string;
					spfId: number | null;
					mppId: number | null;
					customerCreationStartDateTime: string | null;
					customerCreationStartProcess: string | null;
					customerCreationEndDateTime: string | null;
					customerCreationEndProcess: string | null;
					customerCreationDate: string;
					customerCreationDuration: string;
					prospectingStartDateTime: string | null;
					prospectingStartProcess: string | null;
					prospectingEndDateTime: string | null;
					prospectingEndProcess: string | null;
					prospectingDate: string;
					prospectingDuration: string;
					customerCheckingStartDateTime: string | null;
					customerCheckingStartProcess: string | null;
					customerCheckingEndDateTime: string | null;
					customerCheckingEndProcess: string | null;
					customerCheckingDate: string;
					customerCheckingDuration: string;
					praSurveyStartDateTime: string | null;
					praSurveyStartProcess: string | null;
					praSurveyEndDateTime: string | null;
					praSurveyEndProcess: string | null;
					praSurveyDate: string;
					praSurveyDuration: string;
					surveyStartDateTime: string | null;
					surveyStartProcess: string | null;
					surveyEndDateTime: string | null;
					surveyEndProcess: string | null;
					surveyDate: string;
					surveyDuration: string;
					visitReportStartDateTime: string | null;
					visitReportStartProcess: string | null;
					visitReportEndDateTime: string | null;
					visitReportEndProcess: string | null;
					visitReportDate: string | null;
					visitReportDuration: string;
					pipelineHotProspectStartDateTime: string | null;
					pipelineHotProspectStartProcess: string | null;
					pipelineHotProspectEndDateTime: string | null;
					pipelineHotProspectEndProcess: string | null;
					pipelineHotProspectDate: string;
					pipelineHotProspectDuration: string;
					napStartDateTime: string | null;
					napStartProcess: string | null;
					napEndDateTime: string | null;
					napEndProcess: string | null;
					napDate: string;
					napDuration: string;
					resolutionStartDateTime: string | null;
					resolutionStartProcess: string | null;
					resolutionEndDateTime: string | null;
					resolutionEndProcess: string | null;
					resolutionDate: string;
					resolutionDuration: string;
					spfStartDateTime: string | null;
					spfStartProcess: string | null;
					spfEndDateTime: string | null;
					spfEndProcess: string | null;
					spfDate: string;
					spfDuration: string;
					agreementDisbursementStartDateTime: string | null;
					agreementDisbursementStartProcess: string | null;
					agreementDisbursementEndDateTime: string | null;
					agreementDisbursementEndProcess: string | null;
					agreementDisbursementDate: string;
					agreementDisbursementDuration: string;
					applicationSlaStartDateTime: string | null;
					applicationSlaStartProcess: string | null;
					applicationSlaEndDateTime: string | null;
					applicationSlaEndProcess: string | null;
					applicationSla: string;
				}[];
				disbursements: {
					key: number;
					customerId: string;
					customerName: string;
					bmppGroupId: string | null;
					bmppGroupName: string | null;
					frmName: string;
					fbdhName: string;
					divisionName: string;
					plafonType: string;
					orderSource: string;
					plafonAmount: number;
					prospectId: string;
					mppId: number | null;
					spfId: number | null;
					mppNo: string;
					mppStatus: string;
					ntfAmount: number;
					simulationId: number;
					simulationNo: string;
					purchaseOrderId: number;
					purchaseOrderNo: string;
					creditSimulationStartDateTime: string | null;
					creditSimulationStartProcess: string | null;
					creditSimulationEndDateTime: string | null;
					creditSimulationEndProcess: string | null;
					creditSimulationDate: string | null;
					creditSimulationDuration: string | null;
					mppStartDateTime: string | null;
					mppStartProcess: string | null;
					mppEndDateTime: string | null;
					mppEndProcess: string | null;
					mppDate: string | null;
					mppDuration: string;
					purchaseOrderStartDateTime: string | null;
					purchaseOrderStartProcess: string | null;
					purchaseOrderEndDateTime: string | null;
					purchaseOrderEndProcess: string | null;
					purchaseOrderDate: string;
					purchaseOrderDuration: string;
					disbursementStartDateTime: string | null;
					disbursementStartProcess: string | null;
					disbursementEndDateTime: string | null;
					disbursementEndProcess: string | null;
					disbursementDate: string | null;
					disbursementDuration: string;
					disbursementSla: string | null;
				}[];
			}>({
				api,
				body,
			});
		},
	});
};

export type MonitoringSLADetailParam = {
	prosectId?: string;
	purchaseOrderId?: number;
	mppId?: number | null;
	spfId?: number | null;
	module: SLASearchModule;
};

export const useMonitoringSLADetail = (param: MonitoringSLADetailParam) => {
	return useQuery({
		queryKey: ['/monitoring-sla/get-sla-detail', param] as const,
		queryFn: ({ queryKey }) => {
			const [api, body] = queryKey;
			return fetcher<{
				customerId: string;
				customerName: string;
				bmppGroupId: string;
				bmppGroupName: string;
				frmName: string;
				fbdhName: string;
				divisionName: string;
				plafonType: string;
				orderSource: string;
				prospectId: string;
				mppNo: string;
				purchaseOrderNo: string;
				purchaseOrderId: number;
				approvalHistories: {
					name: string;
					date: string;
					duration: string;
					process: string;
					processDetail: string;
					status: string;
					notes: string;
					startDateTime: string | null;
					startProcess: string | null;
					endDateTime: string | null;
					endProcess: string | null;
				}[];
			}>({
				api,
				body,
			});
		},
	});
};

export const useMonitoringPlafondSearchOptions = () => {
	return useQuery({
		queryKey: ['/monitoring-plafon/get-search-options'] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<{
				searchByOptions: MasterData[];
			}>({
				api,
			});
		},
	});
};

export const useMonitoringPlafondSearchCustomer = (
	param: FleetGetListParams,
) => {
	return useQuery({
		queryKey: ['/monitoring-plafon/search-customers', param] as const,
		queryFn: ({ queryKey }) => {
			const [api, body] = queryKey;
			return fetcher<
				FleetPaginated<{
					customerId: string;
					customerName: string;
					bmppGroupId: string;
					bmppGroupName: string;
					totalFinancingLimitAmount: number;
				}> & {
					additionalData: {
						nonRevolvingNjfTotalLimitAmount: number;
						nonRevolvingNjfLimitExistingAmount: number;
						nonRevolvingJfTotalLimitAmount: number;
						nonRevolvingJfLimitExistingAmount: number;
						revolvingNjfTotalLimitAmount: number;
						revolvingNjfLimitExistingAmount: number;
						revolvingJfTotalLimitAmount: number;
						revolvingJfLimitExistingAmount: number;
					};
				}
			>({
				api,
				body,
			});
		},
	});
};

export type MonitoringPlafondCustomerDetailParam = {
	search?: FleetSearch;
	customerId: string;
};

export const useMonitoringPlafondCustomerDetail = (
	param: MonitoringPlafondCustomerDetailParam,
) => {
	return useQuery({
		queryKey: ['/monitoring-plafon/get-customer-detail', param] as const,
		queryFn: ({ queryKey }) => {
			const [api, body] = queryKey;
			return fetcher<{
				customerId: string;
				customerName: string;
				mklNo: string;
				spfId: number;
				spfNo: string;
				bmppGroupId: string;
				bmppGroupName: string;
				plafonType: string;
				financingLimitType: string;
				financingLimitInfo: {
					nonRevolvingNjfTotalLimitAmount: number;
					nonRevolvingNjfLimitExistingAmount: number;
					nonRevolvingJfTotalLimitAmount: number;
					nonRevolvingJfLimitExistingAmount: number;
					revolvingNjfTotalLimitAmount: number;
					revolvingNjfLimitExistingAmount: number;
					revolvingJfTotalLimitAmount: number;
					revolvingJfLimitExistingAmount: number;
				};
				plafons: {
					mkLNo: string;
					spfId: number;
					spfNo: string;
					financingLimitAmount: number;
					plafonType: string;
					financingLimitType: string;
					expiredDate: string;
					ntfAmount: number;
					outstandingPrincipalAmount: number;
					outstandingLimitAmount: number;
					activeContractCount: number;
					orderSource: string;
					status: string;
					remainingPlafonPeriod: string;
					resolutionId?: number | null;
				}[];
				searchByOptions: {
					id: string;
					value: string;
				}[];
			}>({
				api,
				body,
			});
		},
	});
};

export type MonitoringPlafondSearchAgreementParam = {
	search?: FleetSearch;
	spfId: number;
};

export const useMonitoringPlafondSearchAgreement = (
	param: MonitoringPlafondSearchAgreementParam,
) => {
	return useQuery({
		queryKey: ['/monitoring-plafon/get-master-agreements', param] as const,
		queryFn: ({ queryKey }) => {
			const [api, body] = queryKey;
			return fetcher<{
				masterAgreements: {
					masterAgreementId: number;
					masterAgreementNo: string;
					financingSchemeId: number;
					financingScheme: string;
					expiredDate: string;
					label: string;
					mpps: {
						mppId: number;
						mppNo: string;
						disbursementDocumentNo: string;
						mppDate: string;
						ntfAmount: number;
						mppApprovalStatus: string;
						mppStatus: string;
					}[];
					searchByOptions: {
						id: string;
						value: string;
					}[];
				}[];
			}>({
				api,
				body,
			});
		},
	});
};

export type MonitoringPlafondSearchPOParam = {
	search?: FleetSearch;
	mppId: number;
};
export const useMonitoringPlafondSearchPO = (
	param: MonitoringPlafondSearchPOParam,
) => {
	return useQuery({
		queryKey: ['/monitoring-plafon/get-purchase-orders', param] as const,
		queryFn: ({ queryKey }) => {
			const [api, body] = queryKey;
			return fetcher<
				{
					simulationId: number;
					simulationNo: string;
					purchaseOrderId: number;
					purchaseOrderNo: string;
					supplierName: string;
					assetName: string;
					tanggalTerbitPo: string;
					tanggalPoExpired: string;
					statusPo: string;
					tanggalUpdate: string | null;
					ntf: number;
					jumlahUnit: number;
				}[]
			>({
				api,
				body,
			});
		},
	});
};

export const useMonitoringCovenantSearchOptions = () => {
	return useQuery({
		queryKey: ['/monitoring-covenant/get-search-options'] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<{
				searchByOptions: MasterData[];
			}>({
				api,
			});
		},
	});
};

export const useMonitoringCovenantSearchCustomer = (
	param: FleetGetListParams,
) => {
	return useQuery<
		FleetPaginated<{
			customerId: string;
			customerName: string;
			bmppGroupId: string;
			bmppGroupName: string;
			isAllowAction: boolean;
		}>
	>({
		queryKey: ['/monitoring-covenant/search-customers', param] as const,
		queryFn: async ({ queryKey }) => {
			const [api, body] = queryKey as readonly [string, FleetGetListParams];
			const res = await fetcher<
				FleetPaginated<{
					customerId: string;
					customerName: string;
					bmppGroupId: string;
					bmppGroupName: string;
					isAllowAction: boolean;
				}>
			>({
				api,
				body,
			});
			return res.data;
		},
	});
};

export const useMonitoringCovenantCustomerDetail = (customerId: string) => {
	return useQuery<{
		customerName: string;
		customerId: string;
		bmppGroupId: string;
		bmppGroupName: string;
		totalRemainingPlafond: number;
		spfList: {
			mklNo: string;
			spfNo: string;
			spfId: number;
			resolutionId: number;
			fbdhName: string;
			frmName: string;
			existingFinancingLimitAmount: number;
			financingLimitType: string;
			plafonType: string;
			expiredDate: string;
			ntfAmount: number;
			outstandingPrincipalAmount: number;
			remainingFinancingLimitAmount: number;
			orderSource: string;
			isAllowCovenantDetailAction: boolean;
			npwpNo: string;
			groupBmppId: string;
			limitType: string;
			installmentAmount: number;
			activeAgreementCount: number;
			contractStatus: string;
			isActive: number;
			fleetJfRemainingPlafonAmount: number;
			fleetNjfRemainingPlafonAmount: number;
			retailJfRemainingPlafonAmount: number;
			retailNtfRemainingPlafonAmount: number;
			totalRemainingPlafonAmount: number;
			fleetJfLimitAmount: number;
			fleetNjfLimitAmount: number;
			retailJfLimitAmount: number;
			retailNjfLimitAmount: number;
			totalLimitAmount: number;
			division: string;
			plafondStatus: string;
		}[];
	}>({
		queryKey: ['/monitoring-covenant/get-customer-detail', customerId] as const,
		queryFn: async ({ queryKey }) => {
			const [api, customerId] = queryKey as readonly [string, string];
			const res = await fetcher<{
				customerName: string;
				customerId: string;
				bmppGroupId: string;
				bmppGroupName: string;
				totalRemainingPlafond: number;
				spfList: {
					mklNo: string;
					spfNo: string;
					spfId: number;
					resolutionId: number;
					fbdhName: string;
					frmName: string;
					existingFinancingLimitAmount: number;
					financingLimitType: string;
					plafonType: string;
					expiredDate: string;
					ntfAmount: number;
					outstandingPrincipalAmount: number;
					remainingFinancingLimitAmount: number;
					orderSource: string;
					isAllowCovenantDetailAction: boolean;
					npwpNo: string;
					groupBmppId: string;
					limitType: string;
					installmentAmount: number;
					activeAgreementCount: number;
					contractStatus: string;
					isActive: number;
					fleetJfRemainingPlafonAmount: number;
					fleetNjfRemainingPlafonAmount: number;
					retailJfRemainingPlafonAmount: number;
					retailNtfRemainingPlafonAmount: number;
					totalRemainingPlafonAmount: number;
					fleetJfLimitAmount: number;
					fleetNjfLimitAmount: number;
					retailJfLimitAmount: number;
					retailNjfLimitAmount: number;
					totalLimitAmount: number;
					division: string;
					plafondStatus: string;
				}[];
			}>({
				api,
				body: {
					id: customerId,
				},
			});
			return res.data;
		},
	});
};

export const useMonitoringCovenantSPFDetail = (
	resolutionId: number,
	spfId?: number,
) => {
	return useQuery<{
		spfId: number;
		resolutionId: number;
		resolutionFile: FleetFile | null;
		customerName: string;
		customerId: string;
		bmppGroupId: string;
		bmppGroupName: string;
		mklNo: string;
		spfNo: string;
		fbdhName: string;
		frmName: string;
		financingLimitType: string;
		plafonType: string;
		orderSource: string;
		napId: number;
		napNo: string;
		prospectId: string;
		ultimateOwner: string;
		searchOptions: MasterData[];
		covenants: {
			id: number;
			covenant: string;
			category: MasterData;
			priority: MasterData;
			repeatFrequency: MasterData;
			repeatFrequencyCustom: {
				id: number;
				repeatValue: number;
				repeatFrequency: MasterData;
				onPattern: {
					dayOfMonth: number;
					month: MasterData;
				};
				onThePattern: {
					position: MasterData;
					dayOfWeek: MasterData;
					month: MasterData;
				};
			};
			monitoringType: MasterData;
			furtherAnalysis: MasterData;
			dueDate: string;
			uploadDocumentDate: string;
			overdueDays: number;
			notes: string;
			fcohNotes: string;
			covenantDocument: {
				id: string;
				name: string;
				path: string;
				url: string;
				format: string;
			};
			status: string;
		}[];
		statusOptions: MasterData[];
	}>({
		queryKey: [
			'/monitoring-covenant/get-spf-detail',
			resolutionId,
			spfId,
		] as const,
		queryFn: async ({ queryKey }) => {
			const [api, resolutionId, spfId] = queryKey as readonly [
				string,
				number,
				number,
			];
			const res = await fetcher<{
				spfId: number;
				resolutionId: number;
				resolutionFile: FleetFile | null;
				customerName: string;
				customerId: string;
				bmppGroupId: string;
				bmppGroupName: string;
				mklNo: string;
				spfNo: string;
				fbdhName: string;
				frmName: string;
				financingLimitType: string;
				plafonType: string;
				orderSource: string;
				napId: number;
				napNo: string;
				prospectId: string;
				ultimateOwner: string;
				searchOptions: MasterData[];
				covenants: {
					id: number;
					covenant: string;
					category: MasterData;
					priority: MasterData;
					repeatFrequency: MasterData;
					repeatFrequencyCustom: {
						id: number;
						repeatValue: number;
						repeatFrequency: MasterData;
						onPattern: {
							dayOfMonth: number;
							month: MasterData;
						};
						onThePattern: {
							position: MasterData;
							dayOfWeek: MasterData;
							month: MasterData;
						};
					};
					monitoringType: MasterData;
					furtherAnalysis: MasterData;
					dueDate: string;
					uploadDocumentDate: string;
					overdueDays: number;
					notes: string;
					fcohNotes: string;
					covenantDocument: {
						id: string;
						name: string;
						path: string;
						url: string;
						format: string;
					};
					status: string;
				}[];
				statusOptions: MasterData[];
			}>({
				api,
				body: {
					resolutionId,
					spfId,
				},
			});
			return res.data;
		},
	});
};

export const useMonitoringUpdateCovenant = () => {
	return useMutation({
		mutationFn: (body: {
			covenantId: number;
			covenantDocument: FilePayload;
			uploadDocumentDate: string;
			notes: string;
			status: string;
		}) => {
			return fetcher({
				api: '/monitoring-covenant/update-covenant',
				body,
			});
		},
	});
};
